<script setup lang="ts">
import {
  onMounted,
  ref,
  computed,
  watch,
  onBeforeUnmount,
  watchEffect,
} from "vue";
import { onBeforeRouteLeave, useRoute } from "vue-router";
import { useI18n } from "vue-i18n";
import moment from "moment";
import store from "@/store";

import {
  getDate,
  getHour,
  getDifference,
  getDiffBreaks,
} from "@/common/utils/dates";

import { toast } from "@/common/utils/toast";

import type { BreakType, BreakPayloadType } from "@/common/types/breaks";

import {
  analysisPatch,
  analysisStart,
  analysisCancel,
  // analysisPause,
  // analysisPauseUpdate,
  // analysisResume,
  analysisStop,
} from "@/common/api/rest/analysis/analysis";

import {
  analysisBreakList,
  analysisBreakCreate,
  analysisBreakPatch,
  analysisBreakStop,
} from "@/common/api/rest/analysis/break";

import {
  baseConfirm,
  baseModal,
  baseSelect,
  baseTextarea,
  svgIcon,
} from "@/common/components";

import { breakForm, dateEditForm } from "@/components";

const route = useRoute();

const { t, tm } = useI18n();

const emit = defineEmits(["update", "update-description"]);

const props = defineProps<{
  accountId: string;
  analysisId: string;
  status: string;
  startDate: string | null;
  startEdited: string | null;
  startEditedMotive: string | null;
  endDate: string | null;
  endEdited: string | null;
  endEditedMotive: string | null;
  breaks: BreakType[] | null;
  canceledDescription: string | null;
}>();

const interval = ref();
const time = ref<string>("0m 0s");
const breakTime = ref<string>("0m 0s");

const description = ref<string>("");
const breakPayload = ref<BreakPayloadType | null>(null);
const breakSelected = ref<BreakType | null>(null);
const editDatePayload = ref<{
  date: string;
  hour: string;
  motive: string;
} | null>(null);

const editedDate = ref<string | null>(null);
const editedDateType = ref<"start" | "end" | null>(null);

const showModalStart = ref(false);
const showModalPause = ref(false);
const showModalResume = ref(false);
const showModalStop = ref(false);
const showModalCancel = ref(false);
const showModalEditDate = ref(false);

const enumAnalysisStatus = computed(
  () => tm("enums.analysis_status") as Record<string, string>,
);

const isEditDate = computed(() => {
  return store.getters.type === "ADMIN" || store.getters.type === "MANAGER";
});

function timeCount() {
  time.value = getDiffBreaks(
    (props.startEdited ?? props.startDate) as string,
    (props.endEdited ?? props.endDate) as string,
    props.breaks || [],
  ).text;
}

function dateDiff(start_date: string, end_date: string | null) {
  if (!start_date) return "0m 0s";

  const diff = getDifference(
    start_date,
    end_date ? end_date : new Date().toISOString(),
  );

  return diff.text;
}

function formatTime() {
  if (props.startDate && props.endDate) {
    timeCount();

    if (interval.value) {
      clearInterval(interval.value);
      interval.value = null;
    }
    return;
  }

  if (props.status === "PAUSED") {
    timeCount();

    if (!props.breaks) return;

    const breakIndex = props.breaks?.length - 1;
    const breakData = props.breaks[breakIndex];

    if (!breakData) return;

    interval.value = setInterval(() => {
      breakTime.value = dateDiff(breakData.start_date, breakData.end_date);
    }, 1000);
    return;
  }

  if (props.startDate && !props.endDate && !interval.value) {
    interval.value = setInterval(() => {
      timeCount();
    }, 1000);
  }
}

// START
function analyseStart() {
  showModalStart.value = true;
}

function analyseStartCancel() {
  showModalStart.value = false;
}

function analyseStartConfirm() {
  showModalStart.value = false;

  analysisStart(props.accountId, props.analysisId)
    .then((response) => {
      const { success, message } = response;

      if (success) {
        toast({ message: message, type: "success" });

        emit("update");
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

// STOP
function analyseStop() {
  showModalStop.value = true;
}

function analyseStopCancel() {
  showModalStop.value = false;
}

function analyseStopConfirm() {
  showModalStop.value = false;

  analysisStop(props.accountId, props.analysisId)
    .then((response) => {
      const { success, message } = response;

      if (success) {
        toast({ message: message, type: "success" });

        clearInterval(interval.value);
        interval.value = null;

        emit("update");
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

// CANCEL
function analyseCancel() {
  showModalCancel.value = true;
}

function analyseCancelCancel() {
  showModalCancel.value = false;
}

function analyseCancelConfirm() {
  showModalCancel.value = false;

  analysisCancel(props.accountId, props.analysisId)
    .then((response) => {
      const { success, message } = response;

      if (success) {
        toast({ message: message, type: "success" });

        clearInterval(interval.value);
        interval.value = null;

        emit("update");
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

// PAUSE
function analysePause() {
  showModalPause.value = true;
}

function analysePauseCancel() {
  breakSelected.value = null;
  breakPayload.value = null;
  showModalPause.value = false;
}

function analysePauseConfirm() {
  if (!breakPayload.value) {
    toast({ message: t("analysis.toast.error.require_fields"), type: "error" });
    return;
  }

  if (!breakPayload.value.motive) {
    toast({
      message: t("analysis.toast.error.required_motive"),
      type: "error",
    });
    return;
  }

  if (breakSelected.value) {
    editBreakConfirm();

    return;
  }

  analysisBreakCreate(props.accountId, {
    evaluation_id: route.params.evaluation,
    analysis_id: props.analysisId,
    motive: breakPayload.value.motive,
    description: breakPayload.value.description,
  })
    .then((response) => {
      const { success, message } = response;

      if (success) {
        analysePauseCancel();
        toast({ message: message, type: "success" });

        clearInterval(interval.value);
        interval.value = null;

        emit("update");
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

// PAUSE EDIT
function editBreak(data: BreakType) {
  breakSelected.value = { ...data };
  breakPayload.value = {
    motive: data.motive,
    description: data.description,
  };

  showModalPause.value = true;
}

function editBreakConfirm() {
  if (!breakSelected.value) return;

  analysisBreakPatch(
    props.accountId,
    breakSelected.value.break_id,
    breakPayload.value,
  )
    .then((response) => {
      const { success, message } = response;

      if (success) {
        analysePauseCancel();
        toast({ message: message, type: "success" });

        emit("update");
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

// DATE
function editDate(type: "start" | "end") {
  editedDateType.value = type;

  if (type === "start") {
    editDatePayload.value = {
      date: moment(props.startEdited || props.startDate).format("YYYY-MM-DD"),
      hour: moment(props.startEdited || props.startDate).format("HH:mm:ss"),
      motive: props.startEditedMotive ?? "",
    };

    showModalEditDate.value = true;
    return;
  }

  editDatePayload.value = {
    date: moment(props.endEdited || props.endDate).format("YYYY-MM-DD"),
    hour: moment(props.endEdited || props.endDate).format("HH:mm:ss"),
    motive: props.endEditedMotive ?? "",
  };

  showModalEditDate.value = true;
}

function editDateCancel() {
  editedDate.value = null;
  editedDateType.value = null;
  showModalEditDate.value = false;
}

function editDateConfirm() {
  if (
    !editDatePayload.value?.date ||
    !editDatePayload.value?.hour ||
    !editDatePayload.value?.motive
  ) {
    toast({ message: "Preencha todos os campos", type: "error" });
    return;
  }

  const payload = {
    [`${editedDateType.value}_edited`]: `${editDatePayload.value?.date} ${editDatePayload.value?.hour}`,
    [`${editedDateType.value}_edited_motive`]: editDatePayload.value?.motive,
  };

  analysisPatch(props.accountId, props.analysisId, payload)
    .then((response) => {
      const { success, message } = response;

      if (success) {
        editDateCancel();
        toast({ message: message, type: "success" });

        emit("update");
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

// RESUME
function analyseResume() {
  showModalResume.value = true;
}

function analyseResumeCancel() {
  showModalResume.value = false;
}

function analyseResumeConfirm() {
  if (!props.breaks) return;

  const breakIndex = props.breaks?.length - 1;
  const breakData = props.breaks[breakIndex];

  if (!breakData) return;

  analysisBreakStop(props.accountId, breakData.break_id)
    .then((response) => {
      const { success, message } = response;

      if (success) {
        toast({ message: message, type: "success" });

        analyseResumeCancel();
        clearInterval(interval.value);
        interval.value = null;

        emit("update");
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function changeDescription() {
  emit("update-description", description.value);
}

watch(
  () => props.startDate,
  () => {
    formatTime();
  },
);

watch(
  () => props.endDate,
  () => {
    formatTime();
  },
);

watch(
  () => props.breaks,
  () => {
    formatTime();
  },
);

watchEffect(() => {
  if (props.canceledDescription) {
    description.value = String(props.canceledDescription || "");
  }
});

onBeforeRouteLeave(() => {
  clearInterval(interval.value);
  interval.value = null;
});

onBeforeUnmount(() => {
  clearInterval(interval.value);
  interval.value = null;
});

onMounted(() => {
  formatTime();
});
</script>

<template>
  <section class="card-analysis-time">
    <template v-if="!props?.startDate && props.status !== 'CANCELED'">
      <div class="actions">
        <button type="button" class="btn btn-primary" @click="analyseStart()">
          <svg-icon icon="play" size="1.5rem" />
          {{ t("common.labels.play").toUpperCase() }}
        </button>

        <button type="button" class="btn btn-danger" @click="analyseCancel()">
          <svg-icon icon="close" size="1.5rem" />
          {{ t("common.labels.cancel").toUpperCase() }}
        </button>
      </div>
    </template>

    <template v-else>
      <template v-if="!props?.endDate && props.status !== 'PAUSED'">
        <div class="actions">
          <button type="button" class="btn btn-black" @click="analyseStop()">
            <svg-icon icon="pause" size="1.5rem" />
            {{ t("common.labels.finish").toUpperCase() }}
          </button>

          <button type="button" class="btn btn-danger" @click="analyseCancel()">
            <svg-icon icon="close" size="1.5rem" />
            {{ t("common.labels.cancel").toUpperCase() }}
          </button>
        </div>
        <div class="separate" />
      </template>

      <div class="infos">
        <div class="line">
          <label>{{ t("common.labels.status") }}:</label>
          <span class="uppercase">
            {{ enumAnalysisStatus[props?.status] }}
          </span>
        </div>

        <div class="line items-center">
          <label>{{ t("analysis.labels.elapsed_time") }}:</label>
          <span class="text-5">
            {{ time }}
          </span>
        </div>

        <div class="line">
          <label>
            <template v-if="props.startEdited">
              <span>
                {{ t("common.labels.start") }}
                <span style="color: darkred"> (editado) </span>:
              </span>
            </template>
            <template v-else> {{ t("common.labels.start") }}: </template>
          </label>
          <div class="date-edit-value">
            <span>
              {{ getDate(props.startEdited ?? props.startDate) }} -
              {{ getHour(props.startEdited ?? props.startDate) }}
            </span>

            <template v-if="props.startDate && props.endDate && isEditDate">
              <button
                type="button"
                class="btn btn-default p-0 w-7 h-7"
                @click="editDate('start')"
              >
                <svg-icon icon="edit" size="1.2rem" />
              </button>
            </template>
          </div>
        </div>

        <div class="line">
          <label>
            <template v-if="props.endEdited">
              <span>
                {{ t("common.labels.termination") }}
                <span style="color: darkred"> (editado) </span>:
              </span>
            </template>
            <template v-else> {{ t("common.labels.termination") }}: </template>
          </label>
          <div class="date-edit-value">
            <span>
              {{ getDate(props.endEdited ?? props?.endDate) }} -
              {{ getHour(props.endEdited ?? props?.endDate) }}
            </span>

            <template v-if="props.startDate && props.endDate && isEditDate">
              <button
                type="button"
                class="btn btn-default p-0 w-7 h-7"
                @click="editDate('end')"
              >
                <svg-icon icon="edit" size="1.2rem" />
              </button>
            </template>
          </div>
        </div>

        <template v-if="props.status === 'CANCELED'">
          <div class="canceled">
            <label> {{ t("analysis.labels.cancellation_reason") }}: </label>
            <div class="w-full">
              <base-textarea
                v-model="description"
                :value="description"
                :placeholder="t('common.placeholders.describe')"
                @blur="changeDescription()"
              />
            </div>
          </div>
        </template>
      </div>

      <div class="separate" />

      <div class="breaks">
        <div class="title">
          {{ t("analysis.labels.breaks") }}
        </div>

        <div class="items">
          <template v-for="item in props.breaks" :key="item.break_id">
            <div class="item">
              <div class="infos">
                <div class="line">
                  <label> {{ t("analysis.labels.elapsed_time") }}: </label>
                  <span>
                    <template v-if="item.status === 'PENDING'">
                      {{ breakTime }}
                    </template>
                    <template v-else>
                      <span>
                        {{ dateDiff(item.start_date, item.end_date) }}
                      </span>
                    </template>
                  </span>
                </div>

                <div class="line">
                  <label> {{ t("common.labels.start") }}: </label>
                  <span>
                    <span>
                      {{ getDate(item.start_date) }} -
                      {{ getHour(item.start_date) }}
                    </span>
                  </span>
                </div>

                <div class="line">
                  <label> {{ t("common.labels.termination") }}: </label>
                  <span>
                    <span>
                      {{ getDate(item.end_date) }} -
                      {{ getHour(item.end_date) }}
                    </span>
                  </span>
                </div>

                <div class="line">
                  <label> {{ t("analysis.labels.motive") }}: </label>
                  <span>
                    <span>
                      {{ item.motive }}
                    </span>
                  </span>
                </div>

                <div class="line">
                  <label> {{ t("common.labels.description") }}: </label>
                  <span>
                    <span>
                      {{ item.description }}
                    </span>
                  </span>
                </div>
              </div>

              <div class="options">
                <button
                  type="button"
                  class="btn btn-default p-0 w-8 h-8"
                  @click="editBreak(item)"
                >
                  <svg-icon icon="edit" size="1.25rem" />
                </button>
              </div>
            </div>
          </template>

          <template v-if="(props.breaks?.length || 0) === 0">
            <div class="empty">
              {{ t("analysis.texts.empty_break") }}
            </div>
          </template>
        </div>

        <template v-if="!props?.endDate">
          <div class="actions">
            <template v-if="props.status !== 'PAUSED'">
              <button
                type="button"
                class="btn btn-black"
                @click="analysePause()"
              >
                <svg-icon icon="pause" size="1.5rem" />
                {{ t("analysis.labels.add_pause").toUpperCase() }}
              </button>
            </template>

            <template v-else>
              <button
                type="button"
                class="btn btn-primary"
                @click="analyseResume()"
              >
                <svg-icon icon="play" size="1.5rem" />
                {{ t("analysis.labels.remove_pause").toUpperCase() }}
              </button>
            </template>
          </div>
        </template>
      </div>
    </template>
  </section>

  <base-confirm
    :show="showModalStart"
    max-width="400px"
    :title="
      t('common.labels.play', {
        concat: t('common.labels.analyse'),
      }).toUpperCase()
    "
    :message="t('analysis.texts.start_analysis')"
    text-align="start"
    @cancel="analyseStartCancel()"
    @confirm="analyseStartConfirm()"
  />

  <base-confirm
    :show="showModalResume"
    max-width="400px"
    :title="t('analysis.modal.title.resume').toUpperCase()"
    :message="t('analysis.modal.message.resume')"
    text-align="start"
    @cancel="analyseResumeCancel()"
    @confirm="analyseResumeConfirm()"
  />

  <base-confirm
    :show="showModalStop"
    max-width="400px"
    :title="
      t('common.labels.finish', {
        concat: t('common.labels.analyse'),
      }).toUpperCase()
    "
    :message="t('analysis.texts.finish_analysis')"
    text-align="start"
    @cancel="analyseStopCancel()"
    @confirm="analyseStopConfirm()"
  />

  <base-confirm
    :show="showModalCancel"
    max-width="400px"
    :title="
      t('common.labels.cancel', {
        concat: t('common.labels.analyse'),
      }).toUpperCase()
    "
    :message="t('analysis.texts.cancel_analysis')"
    text-align="start"
    @cancel="analyseCancelCancel()"
    @confirm="analyseCancelConfirm()"
  />

  <base-modal
    :show="showModalPause"
    :title="t('analysis.labels.add_pause').toUpperCase()"
    :show-button-confirm="true"
    :button-confirm="
      breakSelected
        ? t('common.labels.save')
        : t('common.labels.add').toUpperCase()
    "
    :button-close="t('common.labels.cancel').toUpperCase()"
    @close="analysePauseCancel()"
    @confirm="analysePauseConfirm()"
  >
    <template #content>
      <break-form :data="breakPayload" @update-data="breakPayload = $event" />
    </template>
  </base-modal>

  <base-modal
    :show="showModalEditDate"
    :title="t('analysis.labels.date_edit').toUpperCase()"
    :show-button-confirm="true"
    width="50%"
    :button-confirm="t('common.labels.save').toUpperCase()"
    :button-close="t('common.labels.cancel').toUpperCase()"
    @close="editDateCancel()"
    @confirm="editDateConfirm()"
  >
    <template #content>
      <date-edit-form
        :data="editDatePayload"
        @update-data="editDatePayload = $event"
      />
    </template>
  </base-modal>
</template>

<style lang="scss" scoped>
.card-analysis-time {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  background: var(--white-off);
  padding: 1.25rem;
  border-radius: 0.5rem;

  .actions {
    display: flex;
    flex-direction: row;
    gap: 2rem;
  }

  .separate {
    width: 100%;
    height: 1px;
    background: var(--border);
    margin-block: 0.5rem;
  }

  .infos {
    display: flex;
    width: calc(100% - 2rem);
    flex-direction: column;
    align-items: flex-start;
    justify-content: flex-start;
    gap: 0.5rem;
    padding-block: 1rem;
    padding-inline: 1rem;
    background: #ffffff;

    .line {
      display: grid;
      grid-template-columns: 9rem auto;
      width: 100%;
      gap: 1rem;
      font-size: 1rem;

      label {
        color: var(--text-medium);
      }

      .date-edit-value {
        display: flex;
        flex-direction: row;
        align-items: center;
        gap: 1rem;
      }
    }

    .canceled {
      display: grid;
      grid-template-columns: 9rem auto;
      width: 100%;
      gap: 1rem;
      margin-block-start: 1.5rem;

      label {
        color: var(--text-medium);
      }
    }
  }

  .breaks {
    display: flex;
    width: 100%;
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;

    .title {
      font-size: 1.2rem;
      text-transform: uppercase;
      font-weight: bold;
    }

    .items {
      display: flex;
      width: inherit;
      flex-direction: column;
      gap: 0.75rem;
      margin-block-start: 1.5rem;
      margin-block-end: 2rem;

      .item {
        display: grid;
        width: initial;
        grid-template-columns: 1fr auto;
        gap: 0.5rem;
        padding-block: 0.75rem;
        padding-inline: 1rem;
        background: #ffffff;

        .infos {
          display: flex;
          width: initial;
          flex-direction: column;
          gap: 0.5rem;
          padding: 0;

          .line {
            display: grid;
            grid-template-columns: 8.5rem auto;
            gap: 1rem;

            label {
              color: var(--text-medium);
            }
          }
        }
      }

      .empty {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
      }
    }

    .actions {
      display: flex;
      width: 100%;
      flex-direction: row;
      align-items: center;
      justify-content: center;
      gap: 1rem;
    }
  }
}

@media screen and (max-width: 1024px) {
  .card-analysis-time {
    .infos {
      .line {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: 0.5rem;

        span {
          margin-left: auto;
        }

        .date-edit-value {
          margin-left: auto;
        }
      }
    }
  }
}
</style>
